import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./KeepsakePaywall.module.scss"

/**
 * KeepsakePaywall (`/keepsake`) — a self-contained EXPIRATION / RENEWAL paywall
 * for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/keepsake.md
 *
 * Seven prior expiration concepts exist (Deep Freeze cold-fear ice-meter, Unbroken
 * warm streak calendar, Ahead forking future-road, Keystone masonry-arch drag,
 * Keep Posted split-flap lever, Patchwork quilt running-stitch, Evergreen radial
 * seasons dial). This is none of them on any axis:
 *   - Layout:      a tactile KEEPSAKE PHOTO-DECK on a linen tray — a fanned hand of
 *                  Polaroid memory cards that gathers into one bound keepsake stack.
 *                  Not a centered hero+bullets, not radio-cards+feature-list, not a
 *                  calendar / road / arch / board / quilt / wheel.
 *   - Interaction: RIFFLE-TO-BIND — gather the loose year of cards into a banded
 *                  stack (tap the deck, or pull the ribbon tab). A stack/shuffle
 *                  gesture; priors used scrub / drag-drop / toggle / hold / flip /
 *                  toss / paint / lever / stitch / rotary.
 *   - Value frame: RECIPROCITY + ANNIVERSARY — "your vault has quietly kept every
 *                  memory you made this year; keep them together for the year ahead."
 *                  Not streak, not a hopeful road, not gravity, not seasonal, not
 *                  cold loss.
 *   - Register:    warm, tactile, quiet-gratitude / collector-tender. Not fear,
 *                  pride, mechanical or nurturing-calm.
 *   - CTA framing: "Keep my year together" (togetherness / commitment), not
 *                  unlock / subscribe / start-free.
 *
 * Honest & App Store compliant: renewal keeps the real Premium bundle running for
 * NEW memories (automatic full-resolution backup, private & encrypted, organised
 * and in sync). Nothing is ever deleted on expiration and the copy says so
 * outright. No countdown, no fake scarcity, no fabricated statistics; savings % is
 * exact yearly-vs-monthly arithmetic. Yearly is the featured, default plan and
 * carries the most prominent price; monthly is small and secondary. The deck cards
 * are bundled demo memory photos reused from the app's photo set (no egress).
 */

type PlanKey = "yearly" | "monthly"

// Bundled demo memories reused from the app's memory-photo set (no network).
const CARDS: { src: string; cap: string }[] = [
  { src: "/byheart/m04.jpg", cap: "Spring" },
  { src: "/byheart/m07.jpg", cap: "By the water" },
  { src: "/byheart/m13.jpg", cap: "Golden hour" },
  { src: "/byheart/m08.jpg", cap: "The road trip" },
  { src: "/byheart/m05.jpg", cap: "In the garden" },
  { src: "/byheart/m16.jpg", cap: "Leaf walks" },
  { src: "/byheart/m14.jpg", cap: "The whole year" },
]

const MID = (CARDS.length - 1) / 2

// A neat bound keepsake stack — tight offsets, small alternating tilt.
const keptOf = (i: number) => {
  const s = i - MID
  return {
    x: Math.round(-s * 2.4),
    y: Math.round(i * 6 - 12),
    rotate: (i % 2 === 0 ? -1 : 1) * (2 + (i % 3)),
    scale: 1,
  }
}

// A loose hand of memories, fanned open — every photo on show.
const fanOf = (i: number) => {
  const s = i - MID
  return {
    x: Math.round(s * 48),
    y: Math.round(Math.abs(s) * 9 - 6),
    rotate: Math.round(s * 11),
    scale: 1,
  }
}

// Small line icons, drawn to match the art direction and tint with currentColor.
const IconBackup = () => (
  <svg viewBox="0 0 24 24" width={17} height={17} aria-hidden>
    <path
      d="M7 18a4 4 0 0 1-.4-7.98A5.5 5.5 0 0 1 17.5 9.2 3.9 3.9 0 0 1 17 18H7z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
    />
    <path
      d="M12 15.5V9.7m0 0L9.8 12M12 9.7l2.2 2.3"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const IconPrivate = () => (
  <svg viewBox="0 0 24 24" width={17} height={17} aria-hidden>
    <rect
      x={5}
      y={10.5}
      width={14}
      height={9.5}
      rx={2.2}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
    />
    <path
      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    />
    <circle cx={12} cy={15} r={1.5} fill="currentColor" />
  </svg>
)
const IconSync = () => (
  <svg viewBox="0 0 24 24" width={17} height={17} aria-hidden>
    <path
      d="M5 12a7 7 0 0 1 11.9-5M19 12a7 7 0 0 1-11.9 5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    />
    <path
      d="M16.5 3.5V7H13M7.5 20.5V17H11"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Shield = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M12 2.6l7 2.6v6c0 4.7-3 7.7-7 10-4-2.3-7-5.3-7-10v-6l7-2.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
    />
    <path
      d="M8.6 12.2l2.4 2.4 4.4-4.6"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const KEEPS: { icon: () => JSX.Element; label: string }[] = [
  { icon: IconBackup, label: "Backed up" },
  { icon: IconPrivate, label: "Private" },
  { icon: IconSync, label: "In sync" },
]

const KeepsakePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")
  // bound = the year's cards are gathered into one kept keepsake stack.
  const [bound, setBound] = useState(false)
  const demoDone = useRef(false)

  // Gentle auto-demo: the loose year of memories settles together once on load,
  // so the "riffle-to-bind" mechanic reads at a glance. Then it's the user's.
  useEffect(() => {
    if (reduceMotion) {
      setBound(true)
      demoDone.current = true
      return
    }
    const t = window.setTimeout(() => {
      setBound(true)
      demoDone.current = true
    }, 900)
    return () => window.clearTimeout(t)
  }, [reduceMotion])

  const toggleBound = useCallback(() => {
    setBound((b) => {
      native.logMixpanelEvent("html_keepsake_bind", { bound: !b })
      return !b
    })
  }, [])

  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }

  const handleClose = () => native.postNativeEvent("close")

  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const spring = reduceMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 340, damping: 26 }

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? "Start 7 days free"
        : "Keep my year together"
      : "Keep month to month"

  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  return (
    <div className={styles.page}>
      <div className={styles.grain} aria-hidden />

      <div className={styles.top}>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width={15} height={15} aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className={styles.brand}>
          <span className={styles.brandShield} aria-hidden>
            <Shield size={14} />
          </span>
          Private Photo Vault
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <span className={styles.topSpacer} aria-hidden />
      </div>

      <header className={styles.head}>
        <p className={styles.kicker}>Your year in the vault</p>
        <h1 className={styles.title}>
          A whole year of memories,
          <br />
          kept together.
        </h1>
        <p className={styles.sub}>
          Your vault has quietly kept every photo and video you added this year.
          Keep them together for the year ahead.
        </p>
      </header>

      {/* ── The keepsake deck ─────────────────────────────────────────── */}
      <div className={styles.stageWrap}>
        <div
          className={styles.stage}
          role="button"
          tabIndex={0}
          aria-label={
            bound
              ? "Your year, kept together. Activate to fan the memories out."
              : "A loose year of memories. Activate to gather them together."
          }
          aria-pressed={bound}
          onClick={toggleBound}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              toggleBound()
            }
          }}
          data-bound={bound}
        >
          <div className={styles.tray} aria-hidden />

          <div className={styles.deck}>
            {CARDS.map((c, i) => {
              const t = bound ? keptOf(i) : fanOf(i)
              return (
                <motion.div
                  key={c.src}
                  className={styles.card}
                  style={{ zIndex: CARDS.length - i }}
                  initial={false}
                  animate={t}
                  transition={{
                    ...spring,
                    delay: reduceMotion
                      ? 0
                      : (bound ? i : CARDS.length - i) * 0.028,
                  }}
                >
                  <span
                    className={styles.photo}
                    style={{ backgroundImage: `url(${c.src})` }}
                  />
                  <span className={styles.cap}>{c.cap}</span>
                </motion.div>
              )
            })}

            {/* the keepsake band that binds the stack when kept */}
            <motion.div
              className={styles.band}
              initial={false}
              animate={
                bound
                  ? { opacity: 1, y: 0, rotate: -1.5, scaleX: 1 }
                  : { opacity: 0, y: 40, rotate: -8, scaleX: 0.82 }
              }
              transition={{ ...spring, delay: reduceMotion ? 0 : 0.14 }}
            >
              <span className={styles.bandShield} aria-hidden>
                <Shield size={15} />
              </span>
              <span className={styles.bandText}>
                <b>This year</b>
                <span>kept &amp; safe</span>
              </span>
            </motion.div>
          </div>
        </div>

        {/* the ribbon tab — the pull affordance, sits just under the deck */}
        <div className={styles.ribbonRow}>
          <button
            type="button"
            className={`${styles.ribbon} ${bound ? styles.ribbonTied : ""}`}
            onClick={toggleBound}
          >
            <span className={styles.ribbonDot} aria-hidden />
            {bound
              ? "Kept together — tap to look again"
              : "Tap to keep together"}
          </button>
        </div>

        <p className={styles.status} aria-live="polite">
          {bound
            ? "Bound into one keepsake — every memory this year, kept."
            : "A loose year of memories, waiting to be kept together."}
        </p>
      </div>

      {/* what "keeping" actually does going forward (real Premium bundle) */}
      <ul className={styles.keeps} aria-label="What Premium keeps doing">
        {KEEPS.map(({ icon: Icon, label }) => (
          <li key={label}>
            <span className={styles.keepIcon} aria-hidden>
              <Icon />
            </span>
            {label}
          </li>
        ))}
      </ul>

      {/* ── Plan choice — yearly featured, monthly small & secondary ──── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.planSel : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
          <span className={styles.planMain}>
            <span className={styles.planName}>Keep the whole year</span>
            <span className={styles.planNote}>12 months, kept together</span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo, billed yearly
            </span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.planMonthSel : ""}`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Month to month</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours — nothing is ever deleted. Renewing simply
        keeps your vault filing every new memory, safely and privately.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default KeepsakePaywall
